import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Descriptions, Divider, Space, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import http from '../../http';
import DataTable from '../../components/DataTable/DataTable';

const formatHours = (hours) => {
    if (hours == null) return '-';
    const h = Math.floor(hours);
    const m = Math.floor((hours % 1) * 60);
    const s = Math.floor(((hours % 1) * 60 % 1) * 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const SERVICE_LABELS = {
    excavation: 'Excavation Record',
    boring: 'Boring Guidance',
};

const BILLING_LABELS = {
    hourly: 'Hourly',
    daily: 'Daily',
    full_period: 'Machine Schedule Period',
};

const MachineRent = () => {
    const { id, rentId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [record, setRecord] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!rentId) return;
        setLoading(true);
        http.get(`/api/v1/machines/constructor_machines/${rentId}/`)
            .then((resp) => {
                setRecord(resp.data);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                navigate('/content_not_found', { replace: true });
            });
    }, [rentId]);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <Spin />
            </div>
        );
    }

    const isExcavator = record?.machine_data?.machine_type === 1;

    const detailItems = [
        { label: t('constructor_company_name_field'), children: record?.constructor_data?.company_name || '-' },
        { label: t('constructor_contact_person_field'), children: record?.constructor_data?.contact_person_name || '-' },
        { label: t('lease_history_col4'), children: record?.machine_name || '-' },
        { label: t('lease_history_col5'), children: t(record?.machine_data?.machine_type_text) || '-' },
        { label: t('lease_history_col6'), children: record?.machine_data?.number || '-' },
        { label: t('lease_history_col10'), children: record?.reference_code || '-' },
        { label: t('lease_start_date_original_label'), children: record?.start_date || '-' },
        { label: t('lease_end_date_original_label'), children: record?.end_date || '-' },
        { label: t('lease_start_date_label'), children: record?.active_history?.start_date || '-' },
        { label: t('lease_end_date_label'), children: record?.active_history?.end_date || '-' },
        ...(isExcavator
            ? [
                { label: t('leaseadd_default_attachment_field'), children: record?.default_bucket_text || '-' },
                { label: t('leaseadd_spare_attachment_field'), children: record?.spare_bucket_text || '-' },
            ]
            : []),
    ];

    const extensionColumns = [
        { title: t('extension_request_list_col1'), dataIndex: 'application_date', key: 'application_date' },
        { title: t('extension_request_list_col2'), dataIndex: 'from_date', key: 'from_date' },
        { title: t('extension_request_list_col3'), dataIndex: 'to_date', key: 'to_date' },
        { title: t('extension_request_list_col4'), dataIndex: 'description', key: 'description' },
        {
            title: t('extension_request_list_col5'),
            dataIndex: 'status_text',
            key: 'status_text',
            render: (text) => <div>{t(text)}</div>,
        },
    ];

    const returnColumns = [
        { title: t('lease_cancelrequest_col1'), dataIndex: 'return_date', key: 'return_date' },
        {
            title: t('lease_cancelrequest_col2'),
            dataIndex: 'status_text',
            key: 'status_text',
            render: (text) => <div>{t(text)}</div>,
        },
    ];

    const machineUsageColumns = [
        {
            title: 'Month',
            dataIndex: 'created',
            key: 'created',
            render: (v) =>
                v
                    ? new Date(v).toLocaleDateString(undefined, { year: 'numeric', month: 'long' })
                    : '-',
        },
        {
            title: 'Last Updated',
            dataIndex: 'updated',
            key: 'updated',
            render: (v) => (v ? new Date(v).toLocaleString() : '-'),
        },
        { title: 'Total Hours', dataIndex: 'total_hours', key: 'total_hours', render: (v) => formatHours(v) },
    ];

    const usageLogColumns = [
        { title: 'Service Name', dataIndex: 'log_type', key: 'log_type', render: (v) => SERVICE_LABELS[v] ?? v },
        { title: 'Billing Preference', dataIndex: 'billing_preference', key: 'billing_preference', render: (v) => BILLING_LABELS[v] ?? v },
        { title: 'Manifest', dataIndex: 'manifest_name', key: 'manifest_name', render: (v) => v ?? '-' },
        { title: 'Project', dataIndex: 'partner_project', key: 'partner_project', render: (v) => v ?? '-' },
        { title: 'Total Hours', dataIndex: 'total_hours', key: 'total_hours', render: (v) => formatHours(v) },
        { title: 'Total Days', dataIndex: 'total_days', key: 'total_days', render: (v) => v ?? '-' },
        {
            title: 'Schedule Start',
            dataIndex: 'schedule_from',
            key: 'schedule_from',
            render: (v, row) => (row.billing_preference === 'full_period' && v ? new Date(v).toLocaleDateString() : '-'),
        },
        {
            title: 'Schedule End',
            dataIndex: 'schedule_to',
            key: 'schedule_to',
            render: (v, row) => (row.billing_preference === 'full_period' && v ? new Date(v).toLocaleDateString() : '-'),
        },
        {
            title: 'Assignment Date',
            dataIndex: 'assignment_at',
            key: 'assignment_at',
            render: (v) => (v ? new Date(v).toLocaleDateString() : '-'),
        },
    ];

    return (
        <div style={{ overflowY: 'auto', overflowX: 'hidden', width: '100%', height: '100%' }}>
            <Space style={{ color: 'black', fontWeight: 'bold' }}>
                <Link to={'/machines'}>{t('machinelist_title')}</Link>
                {'>'}
                <Link to={`/machine/${id}`}>{record?.machine_name}</Link>
                {'>'}
                Rent
            </Space>
            <p style={{ fontWeight: 'bold', fontSize: '24px', marginTop: '28px', marginBottom: 0 }}>
                {t('contract_detail_view_title', { contract_code: record?.contract_code })}
            </p>

            <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} size='small' bordered style={{ marginTop: 16 }}>
                {detailItems.map((item) => (
                    <Descriptions.Item key={item.label} label={item.label}>
                        {item.children}
                    </Descriptions.Item>
                ))}
            </Descriptions>

            <Divider style={{ marginTop: '16px' }} />
            <p style={{ fontWeight: 'bold', fontSize: '18px', margin: 0 }}>Machine Usages</p>
            <div>
                <DataTable
                    hideHeader={true}
                    locale={{ emptyText: t('table_no_data_text') }}
                    data={record?.machine_usages ?? []}
                    columns={machineUsageColumns}
                    pagination={true}
                    pageSize={10}
                />
            </div>

            <Divider style={{ marginTop: '16px' }} />
            <p style={{ fontWeight: 'bold', fontSize: '18px', margin: 0 }}>Service Usages</p>
            <div>
                <DataTable
                    hideHeader={true}
                    locale={{ emptyText: t('table_no_data_text') }}
                    data={record?.usage_logs ?? []}
                    columns={usageLogColumns}
                    pagination={true}
                    pageSize={10}
                />
            </div>

            <Divider style={{ marginTop: '16px' }} />
            <p style={{ fontWeight: 'bold', fontSize: '18px', margin: 0 }}>{t('lease_durationchangelist_title')}</p>
            <div>
                <DataTable
                    hideHeader={true}
                    locale={{ emptyText: t('table_no_data_text') }}
                    data={record?.extension_requests ?? []}
                    columns={extensionColumns}
                    pagination={false}
                />
            </div>

            <Divider style={{ marginTop: '16px' }} />
            <p style={{ fontWeight: 'bold', fontSize: '18px', margin: 0 }}>{t('lease_cancelrequestlist_title')}</p>
            <div>
                <DataTable
                    hideHeader={true}
                    locale={{ emptyText: t('table_no_data_text') }}
                    data={record?.return_requests ?? []}
                    columns={returnColumns}
                    pagination={false}
                />
            </div>
        </div>
    );
};

export default MachineRent;
